import React, { useState } from "react";
import { Box, Tooltip, Typography, styled } from "@mui/material";
import { HomeCampusIcon } from "./IconHomeCampus";
import { MagnetIcon } from "./IconMagnet";
import { SectionHeader, SubSectionHeader } from "./styles";
import type { DistrictProgramForHat, DistrictSchoolForHat } from "../../types";

const PROGRAM_CARD_HEIGHT = 70;

const ProgramCard = styled(Box, {
  label: "DistrictProgramCard",
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>(({ selected }) => ({
  flex: "1 0 0",
  minWidth: 0,
  height: PROGRAM_CARD_HEIGHT,
  display: "flex",
  flexDirection: "column",
  gap: 4,
  padding: "6px 8px",
  borderRadius: 8,
  cursor: "pointer",
  backgroundColor: selected ? "#ffffff" : "rgba(255,255,255,0.75)",
  border: selected ? "2px solid rgba(142,51,255,0.6)" : "2px solid transparent",
  transition: "border-color 0.15s, background-color 0.15s",
  "&:hover": {
    backgroundColor: "#ffffff",
  },
}));

const BadgeBox = styled(Box, {
  label: "DistrictMatchBadge",
  shouldForwardProp: (prop) => prop !== "matchType",
})<{ matchType: "BEST_MATCH" | "CLOSE_MATCH" }>(({ matchType }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "0 6px",
  borderRadius: 6,
  height: 18,
  backgroundColor: matchType === "BEST_MATCH" ? "#8e33ff" : "#ffab00",
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 700,
  fontSize: "10px",
  lineHeight: "20px",
  color: matchType === "BEST_MATCH" ? "#ffffff" : "#1c252e",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
  alignSelf: "flex-start",
}));

const ProgramCardName = styled(Typography, {
  label: "DistrictProgramCardName",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 500,
  fontSize: "12px",
  lineHeight: "18px",
  color: "rgba(0,0,0,0.8)",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
}));

const SchoolDetailCard = styled(Box, {
  label: "DistrictSchoolDetailCard",
})(() => ({
  display: "flex",
  flexDirection: "column",
  gap: 4,
  padding: "8px 16px",
  borderRadius: 16,
  border: "1px solid rgba(255,255,255,1)",
  backdropFilter: "blur(30px)",
  backgroundColor: "rgba(255,255,255,0.08)",
  flexShrink: 0,
}));

const SchoolChip = styled(Box, { label: "DistrictSchoolChip" })(() => ({
  display: "flex",
  alignItems: "center",
  gap: 0,
  height: 24,
  paddingLeft: 3,
  paddingRight: 5,
  borderRadius: 8,
  backgroundColor: "rgba(255,255,255,0.05)",
  flex: "1 1 calc(50% - 4px)",
  minWidth: 0,
  overflow: "hidden",
}));

const SchoolChipLabel = styled(Typography, {
  label: "DistrictSchoolChipLabel",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 500,
  fontSize: "13px",
  lineHeight: "18px",
  color: "#ffffff",
  padding: "0 5px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  minWidth: 0,
  flex: 1,
}));

// --- Helper components ---

const SchoolTypeIcon = ({
  matchType,
}: {
  matchType: DistrictSchoolForHat["matchType"];
}) => (
  <Box
    sx={{
      width: 18,
      height: 18,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    {matchType === "HOME_CAMPUS" ? <HomeCampusIcon /> : <MagnetIcon />}
  </Box>
);

// --- Main component ---

export const DistrictProgramsSection = ({
  programs,
}: {
  programs: DistrictProgramForHat[];
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const toggle = (id: string) =>
    setSelectedId((prev) => (prev === id ? null : id));

  const selectedProgram = programs.find((p) => p.id === selectedId) ?? null;

  // Sort: best matches first, close matches next; lay out in rows of 2
  const sorted = [...programs].sort((a, b) => {
    if (a.matchType === b.matchType) return 0;
    return a.matchType === "BEST_MATCH" ? -1 : 1;
  });
  const rows: DistrictProgramForHat[][] = [];
  for (let i = 0; i < sorted.length; i += 2) {
    rows.push(sorted.slice(i, i + 2));
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        flex: "1 0 0",
        minHeight: 0,
      }}
    >
      <SectionHeader>High School Matches</SectionHeader>

      <SubSectionHeader sx={{ mt: 0.5 }}>
        Programs in Your Area
      </SubSectionHeader>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {rows.map((row, ri) => (
          <Box key={ri} sx={{ display: "flex", gap: 1 }}>
            {row.map((program) => (
              <ProgramCard
                key={program.id}
                selected={selectedId === program.id}
                onClick={() => toggle(program.id)}
              >
                <BadgeBox matchType={program.matchType}>
                  {program.matchType === "BEST_MATCH"
                    ? "Best Match"
                    : "Close Match"}
                </BadgeBox>
                <Tooltip
                  placement="top"
                  title={
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 600,
                          lineHeight: "18px",
                        }}
                      >
                        {program.name}
                      </Typography>
                      {program.matchInfo && (
                        <Typography
                          sx={{
                            fontSize: "11px",
                            lineHeight: "16px",
                            opacity: 0.8,
                            mt: 0.25,
                          }}
                        >
                          {program.matchInfo}
                        </Typography>
                      )}
                    </Box>
                  }
                >
                  <ProgramCardName>{program.name}</ProgramCardName>
                </Tooltip>
              </ProgramCard>
            ))}
            {/* Fill empty slot in last row if odd count */}
            {row.length === 1 && <Box sx={{ flex: "1 0 0" }} />}
          </Box>
        ))}
      </Box>

      {selectedProgram && (
        <SchoolDetailCard sx={{ mt: 0.5 }}>
          <Tooltip title={selectedProgram.name} placement="top">
            <Typography
              sx={{
                fontFamily: "Proxima Nova, sans-serif",
                fontWeight: 600,
                fontSize: "18px",
                lineHeight: "28px",
                color: "#ffffff",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {selectedProgram.name}
            </Typography>
          </Tooltip>
          {(() => {
            const isAllSchools = selectedProgram.schoolMatchType === "all";

            return (
              <>
                <Typography
                  sx={{
                    fontFamily: "Proxima Nova, sans-serif",
                    fontWeight: 400,
                    fontSize: "12px",
                    lineHeight: "18px",
                    color: isAllSchools ? "rgba(255,255,255,0.5)" : "#c4cdd5",
                    fontStyle: isAllSchools ? "italic" : "normal",
                  }}
                >
                  {isAllSchools
                    ? selectedProgram.schools.length > 0
                      ? "Not available for your school, but it is available at:"
                      : "Not available for your school"
                    : "Available in these schools:"}
                </Typography>
                {selectedProgram.schools.length > 0 && (
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.5 }}
                  >
                    {selectedProgram.schools.map((school) => (
                      <Tooltip
                        key={school.id}
                        title={school.name}
                        placement="top"
                      >
                        <SchoolChip>
                          <SchoolTypeIcon matchType={school.matchType} />
                          <SchoolChipLabel>{school.name}</SchoolChipLabel>
                        </SchoolChip>
                      </Tooltip>
                    ))}
                  </Box>
                )}
              </>
            );
          })()}
        </SchoolDetailCard>
      )}
    </Box>
  );
};
